import React, { Fragment, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import dynamic from 'next/dynamic';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

// Components
import Icon from '@/components/base/Icon';
import { DeletePostModal } from '@/components/pages/scheduler/deletePostModal/DeletePostModal';

const PostEventSettings = dynamic(
    () => import('@/components/pages/scheduler/calendar/PostEventSettings')
);
const PopoverContent = ({ children, update }) => {
    useEffect(() => {
        // Position problem so force the update to display the content at the right place
        if (typeof update === 'function') {
            setTimeout(() => update(), 50);
        }
    }, []);

    return (
        <div className="rounded-md border border-icoGray-300 bg-white shadow-icoM dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
            {children}
        </div>
    );
};

const PostEventSettingsPopover = ({
    post,
    onClickEditPost = () => {
        /** */
    },
    onClickReSchedulePost = () => {
        /** */
    },
    onPreview = () => {
        /** */
    },
    mutateSchedulerPosts = () => {
        /** */
    },
    userTimezone,
    disablePopover = false
}) => {
    const { isOpen: isOpenDeleteModal, toggle: toggleDeleteModal } = useToggle();
    const [referenceElement, setReferenceElement] = useState();
    const [popperElement, setPopperElement] = useState();
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-end',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [3, -3]
                }
            }
        ]
    });

    return (
        <>
            <Popover as={Fragment}>
                {({ open }) => (
                    <>
                        <PopoverButton
                            data-testid="post-settings"
                            ref={!disablePopover ? setReferenceElement : null}
                            className="absolute right-0 top-0 z-1 h-4 w-m align-middle text-xxs text-icoGray-500 focus:ring-0 focus:ring-offset-0 dark:text-icoDarkMode-wolf">
                            <Icon icon="dots" />
                        </PopoverButton>
                        {ReactDOM.createPortal(
                            <div
                                ref={setPopperElement}
                                style={styles.popper}
                                {...attributes.popper}
                                className="z-30">
                                <Transition
                                    show={open}
                                    as={Fragment}
                                    enter="ease-out duration-200"
                                    enterFrom="opacity-0 translate-y-1"
                                    enterTo="opacity-100 translate-y-0"
                                    leave="ease-in duration-150"
                                    leaveFrom="opacity-100 translate-y-0"
                                    leaveTo="opacity-0 translate-y-1">
                                    <PopoverPanel>
                                        <PopoverContent update={update}>
                                            <PostEventSettings
                                                post={post}
                                                timezone={userTimezone}
                                                onClickEditPost={onClickEditPost}
                                                onClickReSchedulePost={onClickReSchedulePost}
                                                onPreview={onPreview}
                                                toggleDeleteModal={toggleDeleteModal}
                                            />
                                        </PopoverContent>
                                    </PopoverPanel>
                                </Transition>
                            </div>,
                            document.querySelector('.fc-scroller.fc-scroller-liquid-absolute') ||
                                document.body
                        )}
                    </>
                )}
            </Popover>
            <DeletePostModal
                isOpen={isOpenDeleteModal}
                toggle={toggleDeleteModal}
                post={post}
                deleteSuccess={() => {
                    mutateSchedulerPosts((prevSchedulerPosts) =>
                        prevSchedulerPosts.filter((item) => parseInt(item.id) !== parseInt(post.id))
                    );
                }}
            />
        </>
    );
};

PostEventSettingsPopover.propTypes = {};

export default PostEventSettingsPopover;
